/**
 * One item in the feed. A kind pill, who made it, what state it is in, the
 * tags on it. A lookup shows what it resolved to; a failed one shows why.
 */
import type { BoardroomItem, BoardroomPersona, BoardroomVerdictValue } from '../../types';
import TagChip from './TagChip';
import { priceLabel } from './pricing';
import { KIND_LABELS, isLookup, kindTone, sourceUrl, statusLabel } from './kinds';
import { feedTime, feedTimeTitle } from './feedTime';
import { FetchLogLine } from './FetchLog';

function statusTone(item: BoardroomItem): string {
  switch (item.status) {
    case 'RESERVED':
    case 'IN_PROGRESS':
      return 'text-amber-700 dark:text-amber-300';
    case 'DONE':
      return 'text-emerald-700 dark:text-emerald-300';
    case 'FAILED':
      return 'text-red-600 dark:text-red-400';
    case 'KILLED':
      return 'text-gray-400 line-through';
    default:
      return 'text-gray-500 dark:text-gray-400';
  }
}

/** The skeptic's (or your) ruling, derived from the latest VERDICT child. No
 *  badge means nobody has checked the item — a different fact from "no critique". */
export function VerdictBadge({ verdict }: { verdict: BoardroomVerdictValue | null }) {
  const [label, tone, title] =
    verdict === 'HOLDS'
      ? ['✓ confident', 'text-emerald-700 dark:text-emerald-300 border-emerald-300 dark:border-emerald-700', 'Validated against its receipts: confident it holds']
      : verdict === 'DOES_NOT_HOLD'
        ? ['✗ not confident', 'text-red-700 dark:text-red-300 border-red-300 dark:border-red-700', 'Validated against its receipts: not confident it holds']
        : ['? unverifiable', 'text-gray-500 dark:text-gray-400 border-gray-300 dark:border-gray-600', 'No receipt to check it against'];
  return (
    <span className={`px-1.5 py-0.5 rounded-full border text-[11px] ${tone}`} title={title} data-testid="verdict-badge">
      {label}
    </span>
  );
}

export function whoMade(item: BoardroomItem, personas: Map<number, BoardroomPersona>): string {
  if (item.created_by_persona_id == null) return 'you';
  return personas.get(item.created_by_persona_id)?.name ?? `persona #${item.created_by_persona_id}`;
}

const SOURCED = new Set(['EVIDENCE', 'PAIN', 'FEATURE', 'PRICING']);

export default function ItemCard({
  item,
  personas,
  onOpen,
}: {
  item: BoardroomItem;
  personas: Map<number, BoardroomPersona>;
  onOpen: (item: BoardroomItem) => void;
}) {
  const lookup = isLookup(item.kind);
  const summary = typeof item.payload.summary === 'string' ? (item.payload.summary as string) : null;
  const working = item.status === 'RESERVED' || item.status === 'IN_PROGRESS';
  // Where a review, pain, feature or price was read. A company's own address
  // is its website, not a source, so only these kinds get the link.
  const source = SOURCED.has(item.kind) ? sourceUrl(item) : null;

  return (
    <button
      type="button"
      onClick={() => onOpen(item)}
      className={`w-full text-left border rounded-lg px-3 py-2 bg-white dark:bg-gray-800 hover:border-indigo-300 dark:hover:border-indigo-700 transition ${
        item.status === 'KILLED'
          ? 'border-gray-100 dark:border-gray-800 opacity-60'
          : 'border-gray-200 dark:border-gray-700'
      }`}
      data-testid={`item-${item.id}`}
    >
      <div className="flex items-center gap-2 text-xs">
        <span className={`px-1.5 py-0.5 rounded font-medium ${kindTone(item.kind)}`}>
          {KIND_LABELS[item.kind]}
        </span>
        <span className="text-gray-500 dark:text-gray-400">{whoMade(item, personas)}</span>
        {item.verdict && <VerdictBadge verdict={item.verdict.verdict} />}
        {/* WHEN IT LANDED. The feed is read as a transcript while a dig runs,
            so the question asked of every row is "is this new since I looked?".
            Clock time for today, date and time for anything older; the full
            stamp with seconds is in the tooltip, because two items a minute
            apart read as simultaneous and their order is the thing checked. */}
        <span
          className="ml-auto tabular-nums text-gray-400 dark:text-gray-500"
          title={feedTimeTitle(item.created_at)}
          data-testid="item-time"
        >
          {feedTime(item.created_at)}
        </span>
        <span className={`flex items-center gap-1 ${statusTone(item)}`}>
          {working && (
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-current animate-pulse" aria-hidden />
          )}
          {statusLabel(item.kind, item.status)}
          {working && item.worker_kind === 'PERSONA' && item.worker_id && (
            <span className="text-gray-400">
              · {personas.get(Number(item.worker_id))?.name ?? 'a persona'}
            </span>
          )}
        </span>
      </div>
      <div className={`mt-1 text-sm ${item.status === 'KILLED' ? 'line-through text-gray-500' : 'text-gray-900 dark:text-gray-100'}`}>
        {item.title}
        {source && (
          /* A span, not a link: the whole card is a button, and a link
             inside a button is not allowed. Opens the page; does not open
             the drawer. */
          <span
            role="link"
            tabIndex={0}
            title={source}
            onClick={e => {
              e.stopPropagation();
              window.open(source, '_blank', 'noopener,noreferrer');
            }}
            onKeyDown={e => {
              if (e.key !== 'Enter') return;
              e.stopPropagation();
              e.preventDefault();
              window.open(source, '_blank', 'noopener,noreferrer');
            }}
            className="ml-1 text-xs text-indigo-600 dark:text-indigo-400 hover:underline cursor-pointer"
            data-testid="item-source"
          >
            {source.replace(/^https?:\/\/(www\.)?/, '').split('/')[0]} ↗
          </span>
        )}
      </div>
      {item.kind === 'PRICING' && (
        <div className="mt-0.5 text-xs font-medium text-orange-700 dark:text-orange-300" data-testid="price-label">
          {priceLabel(item.payload)}
        </div>
      )}
      {item.body && !lookup && (
        <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-300 line-clamp-3 whitespace-pre-line">
          {item.body}
        </p>
      )}
      {lookup && summary && item.status === 'DONE' && (
        <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-300 line-clamp-3">{summary}</p>
      )}
      {item.error && (
        <p className="mt-0.5 text-xs text-red-600 dark:text-red-400 line-clamp-2">{item.error}</p>
      )}
      {/* WHAT EACH FETCHER GOT, on the card, so a feed filtered to "failed" reads as a
          list of reasons. The drawer has every try. */}
      {lookup && <FetchLogLine payload={item.payload} />}
      {item.tags.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {/* Not buttons: the whole card is one. */}
          {item.tags.map(t => (
            <TagChip
              key={t.id}
              dimension={t.dimension}
              value={t.value}
              showDimension
              approved={!!t.approved_at}
              title={`${t.dimension.toLowerCase().replace(/_/g, ' ')}: ${t.value} · ${t.approved_at ? 'approved' : 'unverified'}`}
            />
          ))}
        </div>
      )}
    </button>
  );
}
